import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation, useApi } from '../hooks/useApi';
import { upgradeRequestAPI, packageAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const UpgradeRequestForm = ({ onClose, onSuccess, editData = null }) => {
  const { user, loading: authLoading } = useAuth();
  const isEditing = !!editData;
  const [formData, setFormData] = useState(() => {
    // Auto-fill logic
    let currentBandwidth = '';
    if (editData?.current_bandwidth) {
      currentBandwidth = editData.current_bandwidth;
    } else if (user?.current_bandwidth) {
      // Convert integer bandwidth from DB to proper format
      const bandwidth = user.current_bandwidth;
      if (bandwidth >= 1000) {
        const gbps = bandwidth / 1000;
        currentBandwidth = `${gbps} Gbps`;
      } else {
        currentBandwidth = `${bandwidth} Mbps`;
      }
    }

    const initialData = {
      account_id: editData?.account_id || user?.account_id || '',
      current_bandwidth: currentBandwidth,
      requested_bandwidth: editData?.requested_bandwidth || '',
      service_type: editData?.service_type || 'Manage Service Dedicated Internet 1 : 1',
      justification: editData?.justification || '',
      site_a_address: editData?.site_a_address || user?.address || '',
      site_b_address: editData?.site_b_address || '',
    };
    
    
    return initialData;
  });

  const [errors, setErrors] = useState({});

  // Fetch available packages from API
  const { data: packagesData, loading: packagesLoading } = useApi(
    () => packageAPI.getAll({ active_only: true }),
    [],
    { immediate: true }
  );

  const packages = packagesData?.data || [];

  // Fetch upgrade options based on current bandwidth
  const currentBandwidthMbps = user?.current_bandwidth || 0;
  const { data: upgradeOptionsData, loading: upgradeOptionsLoading } = useApi(
    () => packageAPI.getUpgradeOptions(currentBandwidthMbps),
    [currentBandwidthMbps],
    { immediate: !!currentBandwidthMbps }
  );

  const upgradeOptions = upgradeOptionsData?.data || [];

  const { mutate: createRequest, loading } = useMutation(
    upgradeRequestAPI.create,
    {
      onSuccess: () => {
        onSuccess?.();
      }
    }
  );

  // Don't render form until user data and packages are loaded
  if (authLoading || !user || packagesLoading || upgradeOptionsLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  const validateForm = () => {
    const newErrors = {};

    if (!formData.account_id.trim()) {
      newErrors.account_id = 'Account ID is required';
    }
    if (!formData.current_bandwidth.trim()) {
      newErrors.current_bandwidth = 'Current bandwidth is required';
    }
    if (!formData.requested_bandwidth.trim()) {
      newErrors.requested_bandwidth = 'Requested bandwidth is required';
    }
    if (!formData.service_type.trim()) {
      newErrors.service_type = 'Service type is required';
    }
    if (formData.justification.trim().length < 10) {
      newErrors.justification = 'Justification must be at least 10 characters';
    }
    if (formData.site_a_address.trim().length < 5) {
      newErrors.site_a_address = 'Site A address must be at least 5 characters';
    }
    if (formData.site_b_address.trim() && formData.site_b_address.trim().length < 10) {
      newErrors.site_b_address = 'Site B address must be at least 10 characters if provided';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const result = await createRequest(formData);
    
    if (result.success) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const serviceTypes = [
    'Manage Service Dedicated Internet 1 : 1',
    'Fiber Optic',
    'Dedicated Internet',
    'MPLS',
    'Metro Ethernet',
    'Wireless',
    'Other'
  ];

  // Helper function to convert bandwidth string to Mbps for comparison
  const bandwidthToMbps = (bandwidth) => {
    if (!bandwidth) return 0;
    
    if (bandwidth.includes('Gbps')) {
      const value = parseFloat(bandwidth.replace(' Gbps', ''));
      return value * 1000;
    } else if (bandwidth.includes('Mbps')) {
      return parseInt(bandwidth.replace(' Mbps', ''));
    }
    return 0;
  };

  // Get available bandwidth packages from API
  const allBandwidthOptions = packages.map(pkg => pkg.formatted_display);

  // Use upgrade options from API (already filtered for meaningful upgrades)
  const requestedBandwidthOptions = upgradeOptions.map(pkg => pkg.formatted_display);

  // Calculate upgrade cost if both current and requested bandwidth are selected
  const upgradeCostInfo = (() => {
    if (!formData.current_bandwidth || !formData.requested_bandwidth) return null;
    
    const currentPkg = packages.find(pkg => pkg.formatted_display === formData.current_bandwidth);
    const requestedPkg = upgradeOptions.find(pkg => pkg.formatted_display === formData.requested_bandwidth);
    
    if (!currentPkg || !requestedPkg) return null;
    
    return {
      currentPrice: currentPkg.price,
      targetPrice: requestedPkg.price,
      upgradeCost: requestedPkg.price - currentPkg.price,
      currentPackage: currentPkg,
      targetPackage: requestedPkg
    };
  })();

  // Helper function to format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit Upgrade Request' : 'New Bandwidth Upgrade Request'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="account_id" className="form-label">
                Account ID *
              </label>
              <input
                id="account_id"
                name="account_id"
                type="text"
                className={`form-input bg-gray-50 ${errors.account_id ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                value={formData.account_id}
                onChange={handleChange}
                disabled={true}
                readOnly
              />
              {errors.account_id && (
                <p className="mt-1 text-sm text-red-600">{errors.account_id}</p>
              )}
            </div>

            <div>
              <label htmlFor="service_type" className="form-label">
                Service Type *
              </label>
              <select
                id="service_type"
                name="service_type"
                className={`form-input bg-gray-50 ${errors.service_type ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                value={formData.service_type}
                onChange={handleChange}
                disabled={true}
              >
                <option value="Manage Service Dedicated Internet 1 : 1">Manage Service Dedicated Internet 1 : 1</option>
              </select>
              {errors.service_type && (
                <p className="mt-1 text-sm text-red-600">{errors.service_type}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="current_bandwidth" className="form-label">
                Current Bandwidth *
              </label>
              <select
                id="current_bandwidth"
                name="current_bandwidth"
                className={`form-input bg-gray-50 ${errors.current_bandwidth ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                value={formData.current_bandwidth}
                onChange={handleChange}
                disabled={true}
              >
                <option value="">Select current bandwidth</option>
                {allBandwidthOptions.map(bw => (
                  <option key={bw} value={bw}>{bw}</option>
                ))}
              </select>
              {errors.current_bandwidth && (
                <p className="mt-1 text-sm text-red-600">{errors.current_bandwidth}</p>
              )}
            </div>

            <div>
              <label htmlFor="requested_bandwidth" className="form-label">
                Requested Bandwidth *
              </label>
              <select
                id="requested_bandwidth"
                name="requested_bandwidth"
                className={`form-input ${errors.requested_bandwidth ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                value={formData.requested_bandwidth}
                onChange={handleChange}
              >
                <option value="">Select requested bandwidth</option>
                {requestedBandwidthOptions.length > 0 ? (
                  requestedBandwidthOptions.map(bw => {
                    const pkg = upgradeOptions.find(p => p.formatted_display === bw);
                    return (
                      <option key={bw} value={bw}>
                        {bw} - {pkg?.formatted_price || 'N/A'}
                      </option>
                    );
                  })
                ) : (
                  <option value="" disabled>No meaningful upgrades available</option>
                )}
              </select>
              {errors.requested_bandwidth && (
                <p className="mt-1 text-sm text-red-600">{errors.requested_bandwidth}</p>
              )}
            </div>
          </div>

          {upgradeCostInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-3">Cost Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Package ({formData.current_bandwidth}):</span>
                  <span className="font-medium">{formatPrice(upgradeCostInfo.currentPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Target Package ({formData.requested_bandwidth}):</span>
                  <span className="font-medium">{formatPrice(upgradeCostInfo.targetPrice)}</span>
                </div>
                <div className="border-t border-blue-200 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="text-blue-900 font-medium">Upgrade Cost:</span>
                    <span className="text-blue-900 font-bold text-lg">
                      {formatPrice(upgradeCostInfo.upgradeCost)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="justification" className="form-label">
              Justification/Reason for Upgrade *
            </label>
            <textarea
              id="justification"
              name="justification"
              rows={4}
              className={`form-input ${errors.justification ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Please provide a detailed justification for the bandwidth upgrade..."
              value={formData.justification}
              onChange={handleChange}
            />
            <p className="mt-1 text-sm text-gray-500">
              {formData.justification.length}/1000 characters (minimum 10)
            </p>
            {errors.justification && (
              <p className="mt-1 text-sm text-red-600">{errors.justification}</p>
            )}
          </div>

          <div>
            <label htmlFor="site_a_address" className="form-label">
              Site A Address *
            </label>
            <textarea
              id="site_a_address"
              name="site_a_address"
              rows={3}
              className={`form-input ${errors.site_a_address ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Enter complete address for Site A..."
              value={formData.site_a_address}
              onChange={handleChange}
            />
            {errors.site_a_address && (
              <p className="mt-1 text-sm text-red-600">{errors.site_a_address}</p>
            )}
          </div>

          <div>
            <label htmlFor="site_b_address" className="form-label">
              Site B Address (Optional)
            </label>
            <textarea
              id="site_b_address"
              name="site_b_address"
              rows={3}
              className={`form-input ${errors.site_b_address ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Enter complete address for Site B (optional)..."
              value={formData.site_b_address}
              onChange={handleChange}
            />
            {errors.site_b_address && (
              <p className="mt-1 text-sm text-red-600">{errors.site_b_address}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </div>
              ) : (
                isEditing ? 'Update Request' : 'Submit Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpgradeRequestForm;